import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { ApiError } from "@/api/client";
import { AuthProvider, useAuth, useMeQuery } from "@/auth/AuthProvider";
import { AcceptInvitePage, EnrollPage, LoginPage, VerifyPage } from "@/auth/AuthPages";
import { Shell } from "@/layout/Shell";
import { RealtimeProvider } from "@/realtime/RealtimeProvider";
import { EmptyState, SkeletonRows } from "@/ui";
import { ShieldOff } from "lucide-react";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const DeviceHealthPage = lazy(() => import("@/pages/DeviceHealthPage"));
const AdsPage = lazy(() => import("@/pages/AdsPage"));
const InboxPage = lazy(() => import("@/pages/inbox/InboxPage"));
const UsersPage = lazy(() => import("@/pages/users/UsersPage"));
const UserDetailPage = lazy(() => import("@/pages/users/UserDetailPage"));
const StaffPage = lazy(() => import("@/pages/StaffPage"));
const OpsPage = lazy(() => import("@/pages/ops/OpsPage"));
const SecurityPage = lazy(() => import("@/pages/security/SecurityPage"));
const ServerKeysPage = lazy(() => import("@/pages/ServerKeysPage"));
const AIPage = lazy(() => import("@/pages/ai/AIPage"));
const ConfigPage = lazy(() => import("@/pages/ConfigPage"));
const LayoutsPage = lazy(() => import("@/pages/layouts/LayoutsPage"));
const ContentPage = lazy(() => import("@/pages/content/ContentPage"));
const BroadcastsPage = lazy(() => import("@/pages/BroadcastsPage"));
const AuditPage = lazy(() => import("@/pages/AuditPage"));

function Guard({ perm, children }: { perm: string; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(perm)) {
    return <EmptyState icon={ShieldOff} title="Not available for your role">Ask a super admin if you need access.</EmptyState>;
  }
  return <>{children}</>;
}

function Home() {
  const { can } = useAuth();
  if (can("analytics.read")) return <Dashboard />;
  if (can("conversations.read.assigned")) return <Navigate to="/inbox" replace />;
  if (can("content.manage")) return <Navigate to="/content" replace />;
  return <EmptyState icon={ShieldOff} title="Welcome">Your role doesn't include any sections yet.</EmptyState>;
}

function Authenticated() {
  const me = useMeQuery();
  const location = useLocation();
  if (me.isPending) return <SkeletonRows rows={8} />;
  if (me.error) {
    const next = me.error instanceof ApiError && me.error.code === "mfa_required" ? "/login/verify" : "/login";
    return <Navigate to={next} replace state={{ from: location.pathname }} />;
  }
  return (
    <AuthProvider me={me.data}>
      <RealtimeProvider>
        <Suspense fallback={<SkeletonRows rows={8} />}>
          <Routes>
            <Route element={<Shell />}>
              <Route index element={<Home />} />
              <Route path="analytics" element={<Guard perm="analytics.read"><AnalyticsPage /></Guard>} />
              <Route path="devices" element={<Guard perm="analytics.read"><DeviceHealthPage /></Guard>} />
              <Route path="ads" element={<Guard perm="settings.manage"><AdsPage /></Guard>} />
              <Route path="inbox" element={<Guard perm="conversations.read.assigned"><InboxPage /></Guard>} />
              <Route path="inbox/:id" element={<Guard perm="conversations.read.assigned"><InboxPage /></Guard>} />
              <Route path="users" element={<Guard perm="users.read.basic"><UsersPage /></Guard>} />
              <Route path="users/:id" element={<Guard perm="users.read.basic"><UserDetailPage /></Guard>} />
              <Route path="staff" element={<Guard perm="staff.manage"><StaffPage /></Guard>} />
              <Route path="ai" element={<Guard perm="ai.manage"><AIPage /></Guard>} />
              <Route path="config" element={<Guard perm="settings.manage"><ConfigPage /></Guard>} />
              <Route path="layouts" element={<Guard perm="settings.manage"><LayoutsPage /></Guard>} />
              <Route path="content" element={<Guard perm="content.manage"><ContentPage /></Guard>} />
              <Route path="broadcasts" element={<Guard perm="notifications.broadcast"><BroadcastsPage /></Guard>} />
              <Route path="audit" element={<Guard perm="audit.read"><AuditPage /></Guard>} />
              <Route path="ops" element={<OpsPage />} />
              <Route path="security" element={<Guard perm="security.read"><SecurityPage /></Guard>} />
              <Route path="keys" element={<Guard perm="credentials.manage"><ServerKeysPage /></Guard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </RealtimeProvider>
    </AuthProvider>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/verify" element={<VerifyPage />} />
      <Route path="/login/enroll" element={<EnrollPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/reset-access" element={<AcceptInvitePage />} />
      <Route path="/*" element={<Authenticated />} />
    </Routes>
  );
}
